import {Icon} from '@benchling/nucleus-ui';
import {createStyles} from '@mantine/core';
import gql from 'graphql-tag';
import React from 'react';

import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {BLUE_LIGHT, BLUE_POWDER_LIGHT} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Menu from 'ui-library/menus/Menu';
import {OptionData} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import SearchableMenu from 'ui-library/menus/SearchableMenu';

import {UnitTypeDropdownQuery} from './__generated/UnitTypeDropdownQuery';
import {getSortedMatches} from './SearchableUnitDropdown';

type GqlUnitType = UnitTypeDropdownQuery['unitTypes'][number];

const useStyles = createStyles({
  infoOption: {
    padding: '6px',
    display: 'flex',
    alignItems: 'center',
    backgroundColor: BLUE_POWDER_LIGHT,
    margin: '-8px -16px -8px -16px',
    borderRadius: '4px',
  },
});

export const UNIT_TYPE_DROPDOWN_QUERY = gql`
  query UnitTypeDropdownQuery @skipAudit @engComponent(name: UNITS) {
    unitTypes {
      ... on UnitTypeNode {
        id
        name
        archiveData {
          archived
          purpose
        }
      }
    }
  }
`;

const NO_UNIT_TYPES_MESSAGE = (
  <div>
    No unit types found. Your tenant admin can configure via <strong>Feature settings</strong> {'>'}{' '}
    <strong>Unit dictionary</strong>.
  </div>
);

const NoUnitTypesInfoMenu = (): JSX.Element => {
  const {classes} = useStyles();

  return (
    <Menu>
      <Option>
        <div className={classes.infoOption}>
          <Icon style={{color: BLUE_LIGHT, marginRight: '8px'}} icon="info-circle" />
          {NO_UNIT_TYPES_MESSAGE}
        </div>
      </Option>
    </Menu>
  );
};

interface GetUnitTypeOptionsParams {
  includeNone?: boolean;
}

export function getUnitTypeOptions(
  allUnitTypes: Array<GqlUnitType>,
  params: GetUnitTypeOptionsParams
): Array<OptionData> {
  const {includeNone} = params;
  const unitTypes = allUnitTypes.filter((ut) => !ut.archiveData.archived);
  const options: Array<OptionData> = [];
  if (includeNone) {
    options.push({name: 'None', value: null});
  }
  unitTypes.forEach((ut) => {
    options.push({
      name: ut.name,
      value: ut,
      pureTextName: ut.name,
    });
  });
  return options;
}

export interface SearchableUnitTypeDropdownProps {
  onSelect: (selected: GqlUnitType | null) => void;
  disabled?: boolean;
  includeNone?: boolean;
  selectedId?: string | null;
  onOpenChange?: (isOpen: boolean) => void;
}

interface QueriedProps {
  unitTypes: Array<GqlUnitType>;
}

export function PresentationalSearchableUnitTypeDropdown(
  props: SearchableUnitTypeDropdownProps & QueriedProps
): JSX.Element {
  const {unitTypes, includeNone} = props;
  const [isOpen, setIsOpen] = React.useState(false);

  const options = getUnitTypeOptions(unitTypes, {
    includeNone,
  });
  const selected = options.find((o) => o.value && o.value.id === props.selectedId)?.value as
    | GqlUnitType
    | undefined;

  return (
    <SelectDropdown
      displayName={selected?.name ?? 'Select...'}
      disabled={props.disabled}
      shouldEllipsize={true}
      isOpen={isOpen}
      onToggle={(nextIsOpen: boolean) => {
        setIsOpen(nextIsOpen);
        props.onOpenChange?.(nextIsOpen);
      }}
      menu={
        options.length === 0 || (options.length === 1 && props.includeNone) ? (
          <NoUnitTypesInfoMenu />
        ) : (
          <SearchableMenu
            selectedValue={selected}
            onSelect={(selected: GqlUnitType | null): void => {
              props.onSelect(selected);
            }}
            optionDatas={options}
            getMatchingOptionDatas={getSortedMatches}
          />
        )
      }
      dataTestAttrs={{component: 'SearchableUnitTypeDropdown', element: 'dropdown'}}
    />
  );
}

export default function SearchableUnitTypeDropdown(props: SearchableUnitTypeDropdownProps): JSX.Element {
  return (
    <BenchlingQuery<UnitTypeDropdownQuery>
      query={UNIT_TYPE_DROPDOWN_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
    >
      {({unitTypes}, {refetch}) => (
        <PresentationalSearchableUnitTypeDropdown {...props} unitTypes={unitTypes} />
      )}
    </BenchlingQuery>
  );
}
